import axios from 'axios';
import {
  getToken,
  isTokenExpired,
  isPublicAuthEndpoint,
  forceLogout,
  shouldLogoutForError,
} from '../utils/authToken';

// Determine API base URL based on environment
const getApiBaseUrl = () => {
  // Always use environment variable if available
  if (process.env.REACT_APP_API_BASE_URL) {
    return process.env.REACT_APP_API_BASE_URL;
  }
  
  // Fallback for development
  return 'http://localhost:5000/api';
};

const API_BASE_URL = getApiBaseUrl();

console.log('🔧 API Configuration:', {
  NODE_ENV: process.env.NODE_ENV,
  REACT_APP_API_BASE_URL: process.env.REACT_APP_API_BASE_URL,
  API_BASE_URL,
  hostname: window.location.hostname,
});

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000, // Increased to 60 seconds for recommendation calls
});

// Simple in-memory cache for recommendations
const recommendationCache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Request interceptor to add default headers + auth token
api.interceptors.request.use((config) => {
  config.headers['Content-Type'] = 'application/json';

  // Attach the auth token (and bail out early if it has already expired).
  const token = getToken();
  if (token && !isPublicAuthEndpoint(config.url)) {
    if (isTokenExpired(token)) {
      forceLogout('expired');
      return Promise.reject(new axios.Cancel('Session expired'));
    }
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Add longer timeout for recommendation endpoints
  if (config.url?.includes('/recommendations') || config.url?.includes('/smart-recommendations')) {
    config.timeout = 90000; // 90 seconds for recommendations
  }

  return config;
});

// Response interceptor for error handling + auto-logout on auth failure
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (shouldLogoutForError(error)) {
      forceLogout('expired');
    }
    console.error('API Error:', error.response?.data || error.message);
    throw error;
  }
);

// Helper function to filter out blacklisted games
const filterBlacklistedGames = (games, blacklist = []) => {
  if (!blacklist || blacklist.length === 0) return games;
  return games.filter(game => !blacklist.includes(game.id));
};

// Search and discovery functions
export const searchGamesOLD = async (filters = {}, page = 1, limit = 20, blacklist = []) => {
  try {
    console.log("🔍 searchGames called with:", { filters, page, limit });
    
    const params = new URLSearchParams();
    
    // Add pagination
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    
    // Add search query
    if (filters.query) {
      params.append('query', filters.query);
    }
    
    // Add session tracking for progressive search results
    if (filters.sessionId) {
      params.append('sessionId', filters.sessionId);
    }
    if (filters.refresh !== undefined) {
      params.append('refresh', filters.refresh.toString());
    }
    if (filters.refreshCount !== undefined) {
      params.append('refreshCount', filters.refreshCount.toString());
    }
    
    // Add filters
    if (filters.genres?.length) {
      params.append('genres', filters.genres.join(','));
    }
    if (filters.platforms?.length) {
      params.append('platforms', filters.platforms.join(','));
    }
    if (filters.platformType) {
      params.append('platformType', filters.platformType);
    }
    if (filters.tags?.length) {
      params.append('tags', filters.tags.join(','));
    }
    if (filters.gameModes?.length) {
      params.append('gameModes', filters.gameModes.join(','));
    }
    if (filters.gameMode) {
      params.append('gameMode', filters.gameMode);
    }
    if (filters.releasedAfter) {
      params.append('releaseYearFrom', filters.releasedAfter);
    }
    if (filters.releaseYearFrom) {
      params.append('releaseYearFrom', filters.releaseYearFrom.toString());
    }
    if (filters.releaseYearTo) {
      params.append('releaseYearTo', filters.releaseYearTo.toString());
    }
    if (filters.releaseDateFrom) {
      params.append('releaseDateFrom', filters.releaseDateFrom);
    }
    if (filters.releaseDateTo) {
      params.append('releaseDateTo', filters.releaseDateTo);
    }
    if (filters.minUserScore) {
      params.append('minUserScore', filters.minUserScore.toString());
    }
    if (filters.maxUserScore) {
      params.append('maxUserScore', filters.maxUserScore.toString());
    }
    if (filters.minCriticScore) {
      params.append('minCriticScore', filters.minCriticScore.toString());
    }
    if (filters.maxCriticScore) {
      params.append('maxCriticScore', filters.maxCriticScore.toString());
    }
    if (filters.sortBy) {
      params.append('sortBy', filters.sortBy);
    }
    // Set search type (default to fuzzy for better results)
    const searchType = filters.searchType || 'fuzzy';
    params.append('searchType', searchType);

    const searchUrl = `/games/search?${params.toString()}`;
    console.log("📡 API URL:", searchUrl);
    
    const response = await api.get(searchUrl);
    console.log("📥 API Response:", response.data);
    
    // Extract the nested data structure
    const responseData = response.data;
    if (responseData.success && responseData.data) {
      const result = {
        games: filterBlacklistedGames(responseData.data.games || [], blacklist),
        pagination: {
          page: responseData.data.currentPage || 1,
          totalPages: responseData.data.totalPages || 1,
          total: responseData.data.totalCount || 0,
          hasMore: responseData.data.hasMore || false
        }
      };
      console.log("✅ searchGames returning:", result.games.length, "games");
      console.log("🔍 First game from search:", result.games[0]);
      return result;
    }
    console.log("❌ No data in API response");
    return { games: [], pagination: { page: 1, totalPages: 1, total: 0 } };
  } catch (error) {
    console.error('Error searching games:', error);
    return { games: [], pagination: { page: 1, totalPages: 1, total: 0 } };
  }
};

export const searchGames = async (filters = {}, page = 1, limit = 5, blacklist = [], refreshSeed = null) => {
  try {
    console.log("🔍 searchGames called with:", { filters, page, limit, refreshSeed });
    
    const params = new URLSearchParams();
    
    // Add pagination
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    
    // Add search query
    if (filters.query) {
      params.append('query', filters.query);
    }
    
    // Add refresh seed for variety (new for refresh functionality)
    if (refreshSeed !== null) {
      params.append('refreshSeed', refreshSeed.toString());
    }
    
    // Keep existing session tracking for backward compatibility
    if (filters.sessionId) {
      params.append('sessionId', filters.sessionId);
    }
    if (filters.refresh !== undefined) {
      params.append('refresh', filters.refresh.toString());
    }
    if (filters.refreshCount !== undefined) {
      params.append('refreshCount', filters.refreshCount.toString());
    }
    
    // Add filters
    if (filters.genres?.length) {
      params.append('genres', filters.genres.join(','));
    }
    if (filters.platforms?.length) {
      params.append('platforms', filters.platforms.join(','));
    }
    if (filters.platformType) {
      params.append('platformType', filters.platformType);
    }
    if (filters.tags?.length) {
      params.append('tags', filters.tags.join(','));
    }
    if (filters.gameModes?.length) {
      params.append('gameModes', filters.gameModes.join(','));
    }
    if (filters.gameMode) {
      params.append('gameMode', filters.gameMode);
    }
    if (filters.releasedAfter) {
      params.append('releaseYearFrom', filters.releasedAfter);
    }
    if (filters.releaseYearFrom) {
      params.append('releaseYearFrom', filters.releaseYearFrom.toString());
    }
    if (filters.releaseYearTo) {
      params.append('releaseYearTo', filters.releaseYearTo.toString());
    }
    if (filters.releaseDateFrom) {
      params.append('releaseDateFrom', filters.releaseDateFrom);
    }
    if (filters.releaseDateTo) {
      params.append('releaseDateTo', filters.releaseDateTo);
    }
    if (filters.minUserScore) {
      params.append('minUserScore', filters.minUserScore.toString());
    }
    if (filters.maxUserScore) {
      params.append('maxUserScore', filters.maxUserScore.toString());
    }
    if (filters.minCriticScore) {
      params.append('minCriticScore', filters.minCriticScore.toString());
    }
    if (filters.maxCriticScore) {
      params.append('maxCriticScore', filters.maxCriticScore.toString());
    }
    if (filters.sortBy) {
      params.append('sortBy', filters.sortBy);
    }
    // Set search type (default to fuzzy for better results)
    const searchType = filters.searchType || 'fuzzy';
    params.append('searchType', searchType);

    const searchUrl = `/games/search?${params.toString()}`;
    console.log("📡 API URL:", searchUrl);
    
    const response = await api.get(searchUrl);
    console.log("📥 API Response:", response.data);
    
    // Extract the nested data structure
    const responseData = response.data;
    if (responseData.success && responseData.data) {
      const result = {
        games: filterBlacklistedGames(responseData.data.games || [], blacklist),
        pagination: {
          page: responseData.data.currentPage || 1,
          totalPages: responseData.data.totalPages || 1,
          total: responseData.data.totalCount || 0,
          hasMore: responseData.data.hasMore || false
        },
        total: responseData.data.totalCount || 0,
        hasMore: responseData.data.hasMore || false
      };
      console.log("✅ searchGames returning:", result.games.length, "games");
      console.log("🔍 First game from search:", result.games[0]);
      return result;
    }
    console.log("❌ No data in API response");
    return { games: [], pagination: { page: 1, totalPages: 1, total: 0 }, total: 0, hasMore: false };
  } catch (error) {
    console.error('Error searching games:', error);
    return { games: [], pagination: { page: 1, totalPages: 1, total: 0 }, total: 0, hasMore: false };
  }
};

// Game detail functions
export const getGameById = async (id) => {
  try {
    const response = await api.get(`/games/${id}`);
    return response.data.success ? response.data.data : null;
  } catch (error) {
    console.error('Error fetching game by ID:', error);
    throw error;
  }
};

export const getGameBySlug = async (slug) => {
  try {
    const response = await api.get(`/games/slug/${slug}`);
    return response.data.success ? response.data.data : null;
  } catch (error) {
    console.error('Error fetching game by slug:', error);
    throw error;
  }
};

// Alias for getGameById for better semantic naming
export const getGameDetails = async (id) => {
  return await getGameById(id);
};

// Recommendation functions with improved caching and error handling
export const getGameRecommendations = async (gameId, limit = 5) => {
  try {
    const cacheKey = `game_recs_${gameId}_${limit}`;
    
    // Check cache first
    const cached = recommendationCache.get(cacheKey);
    if (cached && (Date.now() - cached.timestamp < CACHE_DURATION)) {
      console.log('🚀 Returning cached game recommendations for game', gameId);
      return cached.data;
    }

    console.log('📡 Fetching game recommendations for game', gameId);
    
    const response = await api.get(`/games/${gameId}/recommendations`, {
      params: { limit },
      timeout: 60000 // 60 second timeout
    });
    
    const result = response.data.success ? response.data.data : [];
    
    // Cache the result
    recommendationCache.set(cacheKey, {
      data: result,
      timestamp: Date.now()
    });

    console.log('✅ Game recommendations fetched successfully:', result.length, 'recommendations');
    return result;
    
  } catch (error) {
    console.error('❌ Error fetching game recommendations:', error.message);
    
    // Fallback to similar games if recommendations fail
    if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
      console.log('⏰ Timeout occurred, trying similar games fallback...');
      try {
        return await getSimilarGames(gameId, limit);
      } catch (fallbackError) {
        console.error('💥 Similar games fallback also failed:', fallbackError.message);
      }
    }
    
    return [];
  }
};

export const getSimilarGames = async (gameId, limit = 5) => {
  try {
    const cacheKey = `similar_games_${gameId}_${limit}`;
    
    // Check cache first
    const cached = recommendationCache.get(cacheKey);
    if (cached && (Date.now() - cached.timestamp < CACHE_DURATION)) {
      console.log('🚀 Returning cached similar games for game', gameId);
      return cached.data;
    }

    console.log('📡 Fetching similar games for game', gameId);
    
    const response = await api.get(`/games/${gameId}/similar`, {
      params: { limit },
      timeout: 45000 // 45 second timeout
    });
    
    const result = response.data.success ? response.data.data : [];
    
    // Cache the result
    recommendationCache.set(cacheKey, {
      data: result,
      timestamp: Date.now()
    });

    console.log('✅ Similar games fetched successfully:', result.length, 'games');
    return result;
    
  } catch (error) {
    console.error('❌ Error fetching similar games:', error.message);
    
    // Final fallback to popular games if everything fails
    if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
      console.log('⏰ Timeout occurred, returning empty array');
    }
    
    return [];
  }
};

export const getSmartRecommendations = async (gameId, limit = 5, refresh = 0, userId = null) => {
  try {
    const cacheKey = `smart_recs_${gameId}_${limit}_${refresh}_${userId || 'anonymous'}`;
    
    // Generate or get session ID for tracking shown games across refreshes
    let sessionId = sessionStorage.getItem('recommendation_session');
    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem('recommendation_session', sessionId);
    }
    
    // Check cache first (only if not refreshing)
    if (refresh === 0) {
      const cached = recommendationCache.get(cacheKey);
      if (cached && (Date.now() - cached.timestamp < CACHE_DURATION)) {
        console.log('🚀 Returning cached smart recommendations for game', gameId);
        return { 
          games: cached.data, 
          metadata: cached.metadata || {} 
        };
      }
    }

    const params = new URLSearchParams();
    params.append('limit', limit.toString());
    params.append('refresh', refresh.toString());
    params.append('sessionId', sessionId);
    if (userId) params.append('userId', userId.toString());

    console.log('📡 Fetching smart recommendations for game', gameId, 'with params:', { limit, refresh, userId });

    const response = await api.get(`/games/${gameId}/smart-recommendations?${params.toString()}`, {
      timeout: 90000 // 90 second timeout for this specific call
    });
    
    const games = response.data.success ? response.data.data : [];
    const metadata = response.data.metadata || {};
    
    // Cache the result
    recommendationCache.set(cacheKey, {
      data: games,
      metadata: metadata,
      timestamp: Date.now()
    });

    // Log metadata for debugging
    if (metadata) {
      console.log('📊 API Metadata:', metadata);
      console.log('🎯 Backend wildcard count:', metadata.wildcard_count);
    }
    
    console.log('✅ Smart recommendations fetched successfully:', games.length, 'recommendations');
    console.log('🎲 API wildcards (manual count):', games.filter(r => r.is_wildcard).length);
    
    // Return both games and metadata
    return { games, metadata };
    
  } catch (error) {
    console.error('❌ Error fetching smart recommendations:', error.message);
    
    // Enhanced fallback strategy
    if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
      console.log('⏰ Timeout occurred, trying fallback to regular recommendations...');
      try {
        const fallbackRecs = await getGameRecommendations(gameId, limit);
        console.log('🔄 Fallback recommendations retrieved:', fallbackRecs.length);
        return { games: fallbackRecs, metadata: { algorithm: 'fallback' } };
      } catch (fallbackError) {
        console.error('💥 Fallback also failed:', fallbackError.message);
      }
    }
    
    // Return empty array as last resort
    return { games: [], metadata: {} };
  }
};

export const recordUserInteraction = async (gameId, interactionType, weight = 1) => {
  try {
    const response = await api.post(`/games/${gameId}/interaction`, {
      interactionType,
      weight
    });
    return response.data;
  } catch (error) {
    console.error('Error recording user interaction:', error);
    return { success: false };
  }
};

export const getGameProsAndCons = async (gameId) => {
  try {
    const response = await api.get(`/games/${gameId}/pros-cons`);
    return response.data.success ? response.data.data : null;
  } catch (error) {
    console.error('Error fetching game pros and cons:', error);
    return null;
  }
};

// Build a query-string suffix for the shared discovery filters (platform,
// releaseAge, genre, tag) used by the Browse Games page. Returns '' if none are set.
const buildDiscoveryFilterQuery = (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.platform) params.set('platform', filters.platform);
  if (filters.releaseAge) params.set('releaseAge', filters.releaseAge);
  if (filters.genre) params.set('genre', filters.genre);
  if (filters.tag) params.set('tag', filters.tag);
  const qs = params.toString();
  return qs ? `&${qs}` : '';
};

// Collection functions
export const getPopularGames = async (page = 1, limit = 20, blacklist = [], filters = {}) => {
  try {
    const response = await api.get(`/games/popular?page=${page}&limit=${limit}${buildDiscoveryFilterQuery(filters)}`);
    const responseData = response.data;
    if (responseData.success && responseData.data) {
      return {
        games: filterBlacklistedGames(responseData.data.games || [], blacklist),
        pagination: {
          page: responseData.data.currentPage || page,
          totalPages: responseData.data.totalPages || 1,
          total: responseData.data.totalCount || 0,
          hasMore: responseData.data.hasMore || false
        }
      };
    }
    return { games: [], pagination: { page: 1, totalPages: 1, total: 0 } };
  } catch (error) {
    console.error('Error fetching popular games:', error);
    return { games: [], pagination: { page: 1, totalPages: 1, total: 0 } };
  }
};

export const getRecentGames = async (page = 1, limit = 20, blacklist = [], filters = {}) => {
  try {
    const response = await api.get(`/games/recent?page=${page}&limit=${limit}${buildDiscoveryFilterQuery(filters)}`);
    const responseData = response.data;
    if (responseData.success && responseData.data) {
      return {
        games: filterBlacklistedGames(responseData.data.games || [], blacklist),
        pagination: {
          page: responseData.data.currentPage || page,
          totalPages: responseData.data.totalPages || 1,
          total: responseData.data.totalCount || 0,
          hasMore: responseData.data.hasMore || false
        }
      };
    }
    return { games: [], pagination: { page: 1, totalPages: 1, total: 0 } };
  } catch (error) {
    console.error('Error fetching recent games:', error);
    return { games: [], pagination: { page: 1, totalPages: 1, total: 0 } };
  }
};

export const getClassicGames = async (page = 1, limit = 20, blacklist = [], filters = {}) => {
  try {
    const response = await api.get(`/games/classic?page=${page}&limit=${limit}${buildDiscoveryFilterQuery(filters)}`);
    const responseData = response.data;
    if (responseData.success && responseData.data) {
      return {
        games: filterBlacklistedGames(responseData.data.games || [], blacklist),
        pagination: {
          page: responseData.data.currentPage || page,
          totalPages: responseData.data.totalPages || 1,
          total: responseData.data.totalCount || 0,
          hasMore: responseData.data.hasMore || false
        }
      };
    }
    return { games: [], pagination: { page: 1, totalPages: 1, total: 0 } };
  } catch (error) {
    console.error('Error fetching classic games:', error);
    return { games: [], pagination: { page: 1, totalPages: 1, total: 0 } };
  }
};

export const getTrendingGames = async (page = 1, limit = 20, blacklist = [], filters = {}) => {
  try {
    const response = await api.get(`/games/trending?page=${page}&limit=${limit}${buildDiscoveryFilterQuery(filters)}`);
    const responseData = response.data;
    // Handle direct response format since our trending API returns data directly
    if (responseData.games) {
      return {
        games: filterBlacklistedGames(responseData.games || [], blacklist),
        pagination: {
          page: responseData.pagination?.currentPage || page,
          totalPages: responseData.pagination?.totalPages || 1,
          total: responseData.pagination?.totalCount || 0,
          hasMore: (responseData.pagination?.currentPage || page) < (responseData.pagination?.totalPages || 1)
        }
      };
    }
    return { games: [], pagination: { page: 1, totalPages: 1, total: 0 } };
  } catch (error) {
    console.error('Error fetching trending games:', error);
    return { games: [], pagination: { page: 1, totalPages: 1, total: 0 } };
  }
};

export const getHiddenGems = async (page = 1, limit = 20, blacklist = [], filters = {}) => {
  try {
    const response = await api.get(`/games/hidden-gems?page=${page}&limit=${limit}${buildDiscoveryFilterQuery(filters)}`);
    const responseData = response.data;
    // Handle direct response format
    if (responseData.games) {
      return {
        games: filterBlacklistedGames(responseData.games || [], blacklist),
        pagination: {
          page: responseData.pagination?.currentPage || page,
          totalPages: responseData.pagination?.totalPages || 1,
          total: responseData.pagination?.totalCount || 0,
          hasMore: (responseData.pagination?.currentPage || page) < (responseData.pagination?.totalPages || 1)
        }
      };
    }
    return { games: [], pagination: { page: 1, totalPages: 1, total: 0 } };
  } catch (error) {
    console.error('Error fetching hidden gems:', error);
    return { games: [], pagination: { page: 1, totalPages: 1, total: 0 } };
  }
};

export const getGreatIndies = async (page = 1, limit = 20, blacklist = [], filters = {}) => {
  try {
    const response = await api.get(`/games/great-indies?page=${page}&limit=${limit}${buildDiscoveryFilterQuery(filters)}`);
    const responseData = response.data;
    // Handle direct response format
    if (responseData.games) {
      return {
        games: filterBlacklistedGames(responseData.games || [], blacklist),
        pagination: {
          page: responseData.pagination?.currentPage || page,
          totalPages: responseData.pagination?.totalPages || 1,
          total: responseData.pagination?.totalCount || 0,
          hasMore: (responseData.pagination?.currentPage || page) < (responseData.pagination?.totalPages || 1)
        }
      };
    }
    return { games: [], pagination: { page: 1, totalPages: 1, total: 0 } };
  } catch (error) {
    console.error('Error fetching indie games:', error);
    return { games: [], pagination: { page: 1, totalPages: 1, total: 0 } };
  }
};

export const getIndieGames = async (page = 1, limit = 20) => {
  try {
    // Search for indie games using tags/genres containing 'indie'
    const response = await api.get(`/games/search`, {
      params: { 
        query: 'indie',
        page,
        limit
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching indie games:', error);
    return { games: [], pagination: { page: 1, totalPages: 1, total: 0 } };
  }
};

// Category functions - now search by genre name instead of ID
export const getGamesByGenre = async (genreName, page = 1, limit = 20) => {
  try {
    const response = await api.get(`/games/search`, {
      params: { 
        genres: genreName,
        page,
        limit
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching games by genre:', error);
    return { games: [], pagination: { page: 1, totalPages: 1, total: 0 } };
  }
};

export const getGamesByPlatform = async (platformName, page = 1, limit = 20) => {
  try {
    const response = await api.get(`/games/search`, {
      params: { 
        platforms: platformName,
        page,
        limit
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching games by platform:', error);
    return { games: [], pagination: { page: 1, totalPages: 1, total: 0 } };
  }
};

// Stats
export const getGameStats = async () => {
  try {
    const response = await api.get('/games/stats');
    return response.data.success ? response.data.data : null;
  } catch (error) {
    console.error('Error fetching game stats:', error);
    return null;
  }
};

// Admin functions (would typically require authentication)
export const importGameFromIGDB = async (igdbId) => {
  try {
    const response = await api.post('/games/import/igdb', { igdbId });
    return response.data;
  } catch (error) {
    console.error('Error importing game from IGDB:', error);
    throw error;
  }
};

export const importGameFromRAWG = async (rawgId) => {
  try {
    const response = await api.post('/games/import/rawg', { rawgId });
    return response.data;
  } catch (error) {
    console.error('Error importing game from RAWG:', error);
    throw error;
  }
};

export const importPopularGames = async (limit = 100) => {
  try {
    const response = await api.post('/games/import/popular', { limit });
    return response.data;
  } catch (error) {
    console.error('Error importing popular games:', error);
    throw error;
  }
};

export const generateAllRecommendations = async () => {
  try {
    const response = await api.post('/games/recommendations/generate-all');
    return response.data;
  } catch (error) {
    console.error('Error generating recommendations:', error);
    throw error;
  }
};

// Game management functions
export const updateGame = async (gameId, updates) => {
  try {
    const response = await api.put(`/games/${gameId}`, updates);
    return response.data;
  } catch (error) {
    console.error('Error updating game:', error);
    throw error;
  }
};

export const deleteGame = async (gameId) => {
  try {
    const response = await api.delete(`/games/${gameId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting game:', error);
    throw error;
  }
};

// Search suggestions function
export const getSearchSuggestions = async (query, limit = 5) => {
  try {
    const response = await api.get(`/games/search/suggestions`, {
      params: {
        q: query,
        limit
      }
    });
    return response.data.success ? response.data.data : [];
  } catch (error) {
    console.error('Error fetching search suggestions:', error);
    return [];
  }
};

// Games belonging to a specific tag (dedicated /tags/:tag page), paginated.
export const getGamesByTag = async (tag, page = 1, limit = 20, blacklist = []) => {
  try {
    const response = await api.get('/games/by-tag', {
      params: { tag, page, limit },
    });
    const responseData = response.data;
    if (responseData.success && responseData.data) {
      return {
        games: filterBlacklistedGames(responseData.data.games || [], blacklist),
        pagination: {
          page: responseData.data.currentPage || page,
          totalPages: responseData.data.totalPages || 1,
          total: responseData.data.totalCount || 0,
          hasMore: responseData.data.hasMore || false,
        },
        tag: responseData.data.tag || tag,
      };
    }
    return { games: [], pagination: { page: 1, totalPages: 1, total: 0, hasMore: false }, tag };
  } catch (error) {
    console.error('Error fetching games by tag:', error);
    return { games: [], pagination: { page: 1, totalPages: 1, total: 0, hasMore: false }, tag };
  }
};

// All games by a developer or publisher (dedicated /company/:role/:name page).
export const getGamesByCompany = async (role, name, page = 1, limit = 20, blacklist = []) => {
  try {
    const response = await api.get('/games/by-company', {
      params: { role, name, page, limit },
    });
    const responseData = response.data;
    if (responseData.success && responseData.data) {
      return {
        games: filterBlacklistedGames(responseData.data.games || [], blacklist),
        pagination: {
          page: responseData.data.currentPage || page,
          totalPages: responseData.data.totalPages || 1,
          total: responseData.data.totalCount || 0,
          hasMore: responseData.data.hasMore || false,
        },
        name: responseData.data.name || name,
        role: responseData.data.role || role,
      };
    }
    return { games: [], pagination: { page: 1, totalPages: 1, total: 0, hasMore: false }, name, role };
  } catch (error) {
    console.error('Error fetching games by company:', error);
    return { games: [], pagination: { page: 1, totalPages: 1, total: 0, hasMore: false }, name, role };
  }
};

// Random games function
export const getRandomGames = async (limit = 10, filters = {}, blacklist = []) => {
  try {
    const response = await api.get('/games/random', {
      params: {
        limit,
        ...filters
      }
    });
    const games = response.data.success ? response.data.data : [];
    return {
      games: filterBlacklistedGames(games, blacklist),
      pagination: {
        page: 1,
        totalPages: 1,
        total: games.length,
        hasMore: false
      }
    };
  } catch (error) {
    console.error('Error fetching random games:', error);
    return {
      games: [],
      pagination: {
        page: 1,
        totalPages: 1,
        total: 0,
        hasMore: false
      }
    };
  }
};

// Utility functions
export const searchWithFuzzy = async (query, page = 1, limit = 20) => {
  return searchGames({ query, searchType: 'fuzzy' }, page, limit);
};

export const searchWithFullText = async (query, page = 1, limit = 20) => {
  return searchGames({ query, searchType: 'fulltext' }, page, limit);
};

// Export the axios instance for custom requests
export { api };

// Get available genres for filtering
export const getAvailableGenres = async () => {
  try {
    const response = await api.get('/games/genres');
    return response.data.success ? response.data.data : [];
  } catch (error) {
    console.error('Error fetching genres:', error);
    return ['Action', 'Adventure', 'RPG', 'Strategy', 'Sports', 'Racing', 'Shooter', 'Puzzle'];
  }
};

// Get available platforms for filtering
export const getAvailablePlatforms = async () => {
  try {
    const response = await api.get('/games/platforms');
    return response.data.success ? response.data.data : [];
  } catch (error) {
    console.error('Error fetching platforms:', error);
    return ['PC', 'PlayStation', 'Xbox', 'Nintendo Switch', 'Mobile'];
  }
};

// Get available tags for filtering
export const getAvailableTags = async () => {
  try {
    const response = await api.get('/games/tags');
    return response.data.success ? response.data.data : [];
  } catch (error) {
    console.error('Error fetching tags:', error);
    return ['Singleplayer', 'Multiplayer', 'Co-op', 'Online', 'Offline', 'Story Rich', 'Open World', 'Fantasy', 'Sci-fi', 'Horror'];
  }
};

// Get advanced filters including genres, platforms, tags, and categories
export const getAdvancedFilters = async () => {
  try {
    const response = await api.get('/games/advanced-filters');
    return response.data.success ? response.data.data : null;
  } catch (error) {
    console.error('Error fetching advanced filters:', error);
    return null;
  }
};

// Get user's favourites
export const getUserFavourites = async () => {
  const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
  const response = await api.get('/auth/favourites', {
    headers: {
      Authorization: token ? `Bearer ${token}` : undefined,
    },
  });
  return response.data.favourites || [];
};

// Get user's favourite games with full data
export const getUserFavouriteGames = async () => {
  try {
    const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
    const response = await api.get('/auth/favourites/games', {
      headers: {
        Authorization: token ? `Bearer ${token}` : undefined,
      },
    });
    return response.data.favourites || [];
  } catch (error) {
    console.error('Error fetching favorite games:', error);
    return [];
  }
};

// Add game to favorites
export const addToFavorites = async (gameId) => {
  try {
    const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
    const response = await api.post(`/auth/favourites/${gameId}`, 
      {},
      {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error adding to favorites:', error);
    throw error;
  }
};

// Remove game from favorites
export const removeFromFavorites = async (gameId) => {
  try {
    const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
    const response = await api.delete(`/auth/favourites/${gameId}`, {
      headers: {
        Authorization: token ? `Bearer ${token}` : undefined,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error removing from favorites:', error);
    throw error;
  }
};

// Toggle favorite status
export const toggleFavorite = async (gameId, isFavorite) => {
  if (isFavorite) {
    return await removeFromFavorites(gameId);
  } else {
    return await addToFavorites(gameId);
  }
};

// Analytics functions
export const getStatsOverview = async () => {
  try {
    const response = await api.get('/analytics/stats');
    return response.data;
  } catch (error) {
    console.error('Error getting stats overview:', error);
    return { success: false, error: error.message };
  }
};

export const getTrendingGamesAnalytics = async (limit = 10) => {
  try {
    const response = await api.get(`/analytics/trending?limit=${limit}`);
    return response.data;
  } catch (error) {
    console.error('Error getting trending games analytics:', error);
    return { success: false, error: error.message };
  }
};

export const getHiddenGemsAnalytics = async (limit = 20, minRating = 4.0) => {
  try {
    const response = await api.get(`/analytics/hidden-gems?limit=${limit}&minRating=${minRating}`);
    return response.data;
  } catch (error) {
    console.error('Error getting hidden gems analytics:', error);
    return { success: false, error: error.message };
  }
};

export const trackGameInteraction = async (gameId, interactionType, weight = 1.0, metadata = {}) => {
  try {
    const response = await api.post('/analytics/track', {
      gameId,
      interactionType,
      weight,
      metadata
    });
    return response.data;
  } catch (error) {
    console.error('Error tracking interaction:', error);
    return { success: false, error: error.message };
  }
};

export const submitRecommendationFeedback = async (gameId, recommendedFromGameId, feedbackType, feedbackText) => {
  try {
    const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
    const response = await api.post('/analytics/feedback', {
      gameId,
      recommendedFromGameId,
      feedbackType,
      feedbackText
    }, {
      headers: {
        Authorization: token ? `Bearer ${token}` : undefined,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error submitting feedback:', error);
    return { success: false, error: error.message };
  }
};

// Toplist functions
export const generateToplist = async (filters, limit = 25) => {
  try {
    const response = await api.post(`/toplist/generate?limit=${limit}`, filters);
    return response.data;
  } catch (error) {
    console.error('Error generating toplist:', error);
    return { success: false, error: error.message };
  }
};

export const getToplistFilterOptions = async () => {
  try {
    const response = await api.get('/toplist/filter-options');
    return response.data;
  } catch (error) {
    console.error('Error getting filter options:', error);
    return { success: false, error: error.message };
  }
};

export const saveUserToplist = async (title, description, filters, gameIds, isPublic = false) => {
  try {
    const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
    const response = await api.post('/toplist/save', {
      title,
      description,
      filters,
      gameIds,
      isPublic
    }, {
      headers: {
        Authorization: token ? `Bearer ${token}` : undefined,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error saving toplist:', error);
    return { success: false, error: error.message };
  }
};

export const getUserToplists = async (includePublic = true) => {
  try {
    const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
    const response = await api.get(`/toplist/user/mine?includePublic=${includePublic}`, {
      headers: {
        Authorization: token ? `Bearer ${token}` : undefined,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error getting user toplists:', error);
    return { success: false, error: error.message };
  }
};

export const getToplistById = async (toplistId) => {
  try {
    const response = await api.get(`/toplist/${toplistId}`);
    return response.data;
  } catch (error) {
    console.error('Error getting toplist:', error);
    return { success: false, error: error.message };
  }
};

export const getPopularToplists = async (limit = 20) => {
  try {
    const response = await api.get(`/toplist/popular?limit=${limit}`);
    return response.data;
  } catch (error) {
    console.error('Error getting popular toplists:', error);
    return { success: false, error: error.message };
  }
};

// AI Description functions
export const getAIDescription = async (gameId) => {
  try {
    const response = await api.get(`/ai/${gameId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching AI description:', error);
    throw error;
  }
};

export const generateAIDescription = async (gameId, forceRegenerate = false) => {
  try {
    const response = await api.post(`/ai/${gameId}`, {}, {
      params: { forceRegenerate }
    });
    return response.data;
  } catch (error) {
    console.error('Error generating AI description:', error);
    throw error;
  }
};

export const generateBatchAIDescriptions = async (gameIds, maxConcurrent = 3) => {
  try {
    const response = await api.post('/ai/generate-batch', {
      gameIds,
      maxConcurrent
    });
    return response.data;
  } catch (error) {
    console.error('Error generating batch AI descriptions:', error);
    throw error;
  }
};

export const getAIDescriptionStats = async () => {
  try {
    const response = await api.get('/ai/stats');
    return response.data;
  } catch (error) {
    console.error('Error fetching AI description stats:', error);
    throw error;
  }
};

export const checkAIServiceHealth = async () => {
  try {
    const response = await api.get('/ai/health');
    return response.data;
  } catch (error) {
    console.error('Error checking AI service health:', error);
    throw error;
  }
};